import { Controller } from 'stimulus'
import {
  loadStripe,
  Stripe,
  StripeElements,
  StripePaymentElement,
} from '@stripe/stripe-js'
import { getLogger } from '@blueberry/observability'

const logger = getLogger('balance/balance-payment-update_controller')

/**
 * Drives the Balance "update payment method" modal: mounts a Stripe Payment Element
 * against a SetupIntent, confirms it, then POSTs the SetupIntent id to the Balance
 * confirm endpoint and renders the returned Turbo Stream (updated billing overview +
 * success alert). Balance-local — does not share px's payment-element controller.
 */
export default class extends Controller {
  static targets = ['submitButton', 'cancelButton', 'error']
  declare submitButtonTarget: HTMLButtonElement
  declare cancelButtonTarget: HTMLButtonElement
  declare errorTarget: HTMLElement
  declare hasErrorTarget: boolean

  static values = {
    clientSecret: String,
    publishableKey: String,
    confirmUrl: String,
    customerEmail: String,
  }
  declare clientSecretValue: string
  declare publishableKeyValue: string
  declare confirmUrlValue: string
  declare customerEmailValue: string

  private stripe: Stripe | null = null
  private elements: StripeElements | null = null
  private paymentElement: StripePaymentElement | null = null

  async connect() {
    if (!this.publishableKeyValue || !this.clientSecretValue) {
      logger.error('Missing Stripe publishable key or client secret')
      return
    }
    try {
      await this.initializeStripe()
    } catch (error) {
      logger.error('Error initializing Stripe', error)
      this.showError('Failed to load the payment form. Please try again.')
    }
  }

  disconnect() {
    // Tear down the Stripe-injected iframes before Turbo snapshots the page.
    // Otherwise the stale iframes are restored from cache on the local origin
    // and Stripe's postMessage handshake (target https://js.stripe.com) fails,
    // leaving the Payment Element unable to mount on the next visit.
    this.paymentElement?.destroy()
    this.paymentElement = null
    this.elements = null
    this.stripe = null
  }

  private async initializeStripe() {
    this.stripe = await loadStripe(this.publishableKeyValue)
    if (!this.stripe) {
      logger.error('Stripe failed to load')
      return
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
    })
    this.paymentElement = this.elements.create('payment', {
      defaultValues: { billingDetails: { email: this.customerEmailValue } },
    })
    // Scope the mount to this controller's element so a stale `#payment-element`
    // left elsewhere in the DOM can't capture the mount.
    const mountTarget = this.element.querySelector('#payment-element')
    if (!mountTarget) {
      logger.error('Payment element mount target not found')
      return
    }
    this.paymentElement.mount(mountTarget as HTMLElement)
  }

  async handleSubmit(event: Event) {
    event.preventDefault()
    if (!this.stripe || !this.elements) {
      this.showError('Payment system not ready. Please try again.')
      return
    }

    this.setLoading(true)
    this.hideError()

    try {
      const { error, setupIntent } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })

      if (error) {
        this.showError(error.message ?? 'Could not save the payment method.')
        this.setLoading(false)
        return
      }

      await this.finalize(setupIntent.id)
    } catch (err) {
      logger.error('Error confirming setup', err)
      this.showError('An unexpected error occurred. Please try again.')
      this.setLoading(false)
    }
  }

  private async finalize(setupIntentId: string) {
    try {
      const response = await fetch(this.confirmUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrfToken(),
          Accept: 'text/vnd.turbo-stream.html',
        },
        body: JSON.stringify({ setup_intent_id: setupIntentId }),
      })

      const contentType = response.headers.get('content-type')
      if (response.ok && contentType?.includes('turbo-stream')) {
        const html = await response.text()
        this.close()
        window.Turbo?.renderStreamMessage(html)
        return
      }

      const data = (await response.json().catch(() => ({}))) as {
        error?: string
      }
      this.showError(data.error ?? 'Failed to update payment method.')
      this.setLoading(false)
    } catch (err) {
      logger.error('Error finalizing payment method', err)
      this.showError('Failed to update payment method. Please try again.')
      this.setLoading(false)
    }
  }

  close() {
    const modal = document.getElementById('bpx-update-payment-modal')
    modal?.classList.remove('modal-open')
  }

  private csrfToken(): string {
    const input = this.element.querySelector<HTMLInputElement>(
      'input[name="csrfmiddlewaretoken"]',
    )
    return input?.value ?? ''
  }

  private setLoading(loading: boolean) {
    this.submitButtonTarget.disabled = loading
    this.cancelButtonTarget.disabled = loading
    this.submitButtonTarget.innerHTML = loading
      ? '<span class="loading loading-spinner loading-sm"></span>'
      : 'Save'
  }

  private showError(message: string) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  private hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }
}

declare global {
  interface Window {
    Turbo?: { renderStreamMessage: (html: string) => void }
  }
}
